import OpenAI from 'openai';
import type { ChatCompletion } from 'openai/resources/chat/completions';

// Initialize the OpenAI client configured for xAI's API
const grokClient = new OpenAI({
  apiKey: process.env.REACT_APP_XAI_API_KEY || 'dummy-key', // React apps need REACT_APP_ prefix
  baseURL: 'https://api.x.ai/v1',
  dangerouslyAllowBrowser: true // Required for browser usage
});

export interface GrokMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface GrokResponse {
  content: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  error?: string;
}

/**
 * Send a message to Grok Code Fast 1 model
 */
export async function sendToGrok(
  messages: GrokMessage[],
  options?: {
    temperature?: number;
    maxTokens?: number;
    stream?: boolean;
  }
): Promise<GrokResponse> {
  try {
    const response = await grokClient.chat.completions.create({
      model: 'grok-code-fast-1', // Using the specific model you requested
      messages: messages,
      temperature: options?.temperature ?? 0.7,
      max_tokens: options?.maxTokens ?? 1000,
      stream: false, // Force non-streaming to avoid type issues
    }) as ChatCompletion;

    if (response.choices && response.choices[0]?.message?.content) {
      return {
        content: response.choices[0].message.content,
        usage: response.usage ? {
          prompt_tokens: response.usage.prompt_tokens,
          completion_tokens: response.usage.completion_tokens,
          total_tokens: response.usage.total_tokens,
        } : undefined
      };
    } else {
      throw new Error('No response content received from Grok');
    }
  } catch (error) {
    console.error('Grok API Error:', error);
    return {
      content: '',
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Quick helper for simple coding questions
 */
export async function askGrokForCode(
  question: string,
  context?: string
): Promise<GrokResponse> {
  const messages: GrokMessage[] = [
    {
      role: 'system',
      content: 'You are Grok Code Fast 1, a highly intelligent coding assistant. Provide concise, practical code solutions and explanations.'
    }
  ];

  if (context) {
    messages.push({
      role: 'user',
      content: `Context: ${context}\n\nQuestion: ${question}`
    });
  } else {
    messages.push({
      role: 'user',
      content: question
    });
  }

  return sendToGrok(messages, { temperature: 0.3 }); // Lower temperature for more consistent code
}

/**
 * Helper for code review and optimization
 */
export async function askGrokToReview(
  code: string,
  language: string = 'typescript'
): Promise<GrokResponse> {
  const messages: GrokMessage[] = [
    {
      role: 'system',
      content: 'You are Grok Code Fast 1, an expert code reviewer. Analyze the code for bugs, performance issues, best practices, and suggest improvements.'
    },
    {
      role: 'user',
      content: `Please review this ${language} code:\n\n\`\`\`${language}\n${code}\n\`\`\``
    }
  ];

  return sendToGrok(messages, { temperature: 0.4 });
}